import React, { useEffect, useState } from 'react';
import axios from 'axios';

// Settings > Texting and Email, SMS Phase 6b: the business details carriers need
// to approve a SnowTrack texting number. Shown to the owner while the server
// says the details can be entered (provisioning.registrationEditable: the
// number is attached and nothing is registered yet, or the business profile
// came back needing a fix).
//
// "Do you have an EIN?" chooses the brand type: Yes registers a Standard brand
// with the business's EIN; No registers a Sole Proprietor brand, verified by a
// code the carrier registry texts to the owner's own mobile.
//
// THE EIN IS WRITE-ONLY. It goes up once in PUT /api/sms/registration and the
// field is cleared whatever the answer. The server keeps its last four digits
// only, so a saved form shows "ending 1234" and the EIN must be typed again to
// resubmit.
const API = `${process.env.REACT_APP_API_URL}/api/sms`;

// SMS Phase 6c: the owner's own policy pages, both or neither (the server
// checks: https, not a home page, at most 500 characters each).
export const POLICY_URL_MAX = 500;
export const POLICY_URLS_HINT = 'Carriers need a privacy policy and terms for your texts. Leave both blank and SnowTrack '
  + 'hosts pages for you. If your website has its own, enter both pages here (the pages themselves, not your home page).';

const BUSINESS_TYPES = [
  'Limited Liability Corporation', 'Corporation', 'Partnership', 'Sole Proprietorship',
  'Co-operative', 'Non-profit Corporation',
];

const EMPTY = {
  hasEin: null,
  legalName: '',
  businessType: '',
  ein: '',
  street: '',
  streetSecondary: '',
  city: '',
  region: '',
  postalCode: '',
  websiteUrl: '',
  privacyPolicyUrl: '',
  termsUrl: '',
  contactFirstName: '',
  contactLastName: '',
  contactEmail: '',
  contactPhone: '',
  soleProprietorMobile: '',
};

const fromSaved = (r) => ({
  ...EMPTY,
  hasEin: r.hasEin,
  legalName: r.legalName || '',
  businessType: r.businessType || '',
  street: r.street || '',
  streetSecondary: r.streetSecondary || '',
  city: r.city || '',
  region: r.region || '',
  postalCode: r.postalCode || '',
  websiteUrl: r.websiteUrl || '',
  privacyPolicyUrl: r.privacyPolicyUrl || '',
  termsUrl: r.termsUrl || '',
  contactFirstName: r.contactFirstName || '',
  contactLastName: r.contactLastName || '',
  contactEmail: r.contactEmail || '',
  contactPhone: r.contactPhone || '',
});

function SmsRegistrationForm({ onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState('');
  const [errorField, setErrorField] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    axios.get(`${API}/registration`)
      .then(({ data }) => {
        if (!live || !data.registration) return;
        setSaved(data.registration);
        setForm(fromSaved(data.registration));
      })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  const set = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const body = { ...form };
    if (body.hasEin !== true) delete body.ein;
    if (body.hasEin !== false) delete body.soleProprietorMobile;
    // Cleared before the request resolves, success or not.
    setForm((f) => ({ ...f, ein: '' }));
    setBusy(true);
    setError('');
    setErrorField(null);
    try {
      const { data } = await axios.put(`${API}/registration`, body);
      setSaved(data.registration);
      if (onSaved) await onSaved(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Your business details could not be saved. Try again.');
      setErrorField(err.response?.data?.field || null);
    } finally {
      setBusy(false);
    }
  };

  const fieldStyle = { marginBottom: '10px' };
  const input = (name, label, props = {}) => (
    <div style={fieldStyle}>
      <label className="form-label" htmlFor={`sms-reg-${name}`}>{label}</label>
      <input id={`sms-reg-${name}`} className="form-input" value={form[name]} onChange={set(name)}
        aria-invalid={errorField === name} data-testid={`sms-reg-${name}`} {...props} />
    </div>
  );

  return (
    <form onSubmit={submit} data-testid="sms-registration-form" style={{ marginTop: '12px' }}>
      <h3 style={{ fontSize: '1rem', margin: '0 0 4px' }}>Your business</h3>
      <p className="text-muted" style={{ fontSize: '0.8125rem', margin: '0 0 12px' }}>
        Phone carriers register every business that sends texts. These details go to them through Twilio and are not shown to customers.
      </p>
      {error && <div className="alert alert-error">{error}</div>}

      <fieldset style={{ border: 'none', padding: 0, margin: '0 0 10px' }} disabled={busy}>
        <legend className="form-label">Do you have an EIN?</legend>
        <label style={{ marginRight: '16px' }}>
          <input type="radio" name="sms-reg-has-ein" checked={form.hasEin === true}
            onChange={() => setForm((f) => ({ ...f, hasEin: true }))} data-testid="sms-reg-ein-yes" /> Yes
        </label>
        <label>
          <input type="radio" name="sms-reg-has-ein" checked={form.hasEin === false}
            onChange={() => setForm((f) => ({ ...f, hasEin: false }))} data-testid="sms-reg-ein-no" /> No, I am a sole proprietor
        </label>
      </fieldset>

      {form.hasEin !== null && (
        <fieldset style={{ border: 'none', padding: 0, margin: 0 }} disabled={busy}>
          {input('legalName', form.hasEin ? 'Legal business name' : 'Your business name', { maxLength: 255, required: true })}

          {form.hasEin && (
            <>
              <div style={fieldStyle}>
                <label className="form-label" htmlFor="sms-reg-businessType">Type of business</label>
                <select id="sms-reg-businessType" className="form-input" value={form.businessType} onChange={set('businessType')}
                  required data-testid="sms-reg-businessType">
                  <option value="">Choose...</option>
                  {BUSINESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              {input('ein', saved?.einLast4 ? `EIN (saved, ending ${saved.einLast4}; type it again to resubmit)` : 'EIN',
                { inputMode: 'numeric', autoComplete: 'off', placeholder: '12-3456789', maxLength: 10, required: true })}
            </>
          )}

          {input('street', 'Street address (no PO Box)', { maxLength: 255, required: true, autoComplete: 'address-line1' })}
          {input('streetSecondary', 'Suite or unit (optional)', { maxLength: 255, autoComplete: 'address-line2' })}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px' }}>
            {input('city', 'City', { maxLength: 100, required: true, autoComplete: 'address-level2' })}
            {input('region', 'State', { maxLength: 2, required: true, placeholder: 'WI', autoComplete: 'address-level1' })}
            {input('postalCode', 'ZIP', { maxLength: 10, required: true, inputMode: 'numeric', autoComplete: 'postal-code' })}
          </div>
          {input('websiteUrl', 'Website (optional)', { type: 'url', maxLength: 2048, placeholder: 'https://' })}
          {input('privacyPolicyUrl', 'Your texting privacy policy page (optional)', { type: 'url', maxLength: POLICY_URL_MAX, placeholder: 'https://' })}
          {input('termsUrl', 'Your texting terms page (optional)', { type: 'url', maxLength: POLICY_URL_MAX, placeholder: 'https://' })}
          <p className="text-muted" style={{ fontSize: '0.8125rem', margin: '-4px 0 10px' }}>{POLICY_URLS_HINT}</p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
            {input('contactFirstName', 'Contact first name', { maxLength: 100, required: true, autoComplete: 'given-name' })}
            {input('contactLastName', 'Contact last name', { maxLength: 100, required: true, autoComplete: 'family-name' })}
          </div>
          {input('contactEmail', 'Contact email', { type: 'email', maxLength: 255, required: true, autoComplete: 'email' })}
          {input('contactPhone', 'Contact phone', { type: 'tel', required: true, autoComplete: 'tel' })}

          {form.hasEin === false && (
            <>
              {input('soleProprietorMobile', saved?.soleProprietorMobileLast4
                ? `Your own mobile (saved, ending ${saved.soleProprietorMobileLast4})` : 'Your own mobile number',
              { type: 'tel', required: true, autoComplete: 'tel' })}
              <p className="text-muted" style={{ fontSize: '0.8125rem', margin: '-4px 0 10px' }}>
                The carrier registry texts this number a code to confirm it is you. Use your personal mobile, not your SnowTrack texting number. You will have 24 hours to reply.
              </p>
            </>
          )}

          <button type="submit" className="btn btn-primary" disabled={busy} data-testid="sms-reg-submit">
            {busy ? 'Sending...' : 'Send to the carriers'}
          </button>
        </fieldset>
      )}
    </form>
  );
}

export default SmsRegistrationForm;
